<template>
  <div class="min-h-screen bg-background flex flex-col">
    <main class="grow flex items-center justify-center">
      <div class="container mx-auto px-4 py-16 text-center">
        <div class="mb-8">
          <h1 class="text-9xl font-bold text-primary">404</h1>
          <h2 class="text-3xl font-semibold mt-4 mb-6">Page Not Found</h2>
          <p class="text-lg text-muted-foreground max-w-md mx-auto mb-8">
            Sorry, we couldn't find the page you're looking for. It might have been moved or doesn't exist.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <NuxtLink 
              to="/" 
              class="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-base font-medium text-primary-foreground shadow-xs hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              Back to Home
            </NuxtLink>
            <button 
              @click="goBack" 
              class="inline-flex items-center justify-center rounded-md border border-input bg-background px-6 py-3 text-base font-medium text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              Go Back
            </button>
          </div>
        </div>
        
        <div class="mt-12">
          <h3 class="text-xl font-medium mb-4">You might be looking for:</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <NuxtLink 
              v-for="link in suggestedLinks" 
              :key="link.url" 
              :to="link.url"
              class="p-4 border border-border rounded-lg hover:bg-accent transition-colors duration-200"
            >
              <h4 class="font-medium mb-2">{{ link.title }}</h4>
              <p class="text-sm text-muted-foreground">{{ link.description }}</p>
            </NuxtLink>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { useSiteStore } from '~/stores/site'

const siteStore = useSiteStore()
const router = useRouter()

definePageMeta({
  layout: 'default'
})

const suggestedLinks = computed(() => [
  {
    title: 'Home Page',
    url: '/',
    description: 'Return to our main page to explore our services and offerings.'
  },
  {
    title: 'Services',
    url: '/services',
    description: 'Discover our comprehensive range of services.'
  },
  {
    title: 'Contact Us',
    url: '/contact',
    description: 'Get in touch with our team for assistance.'
  }
])

// Function to go back in history
const goBack = () => {
  try {
    // Try to go back in history if possible
    if (window.history.length > 1) {
      router.go(-1)
    } else {
      // Fallback to home page
      router.push('/')
    }
  } catch (e) {
    // If any navigation error occurs, safely navigate to home
    router.push('/')
  }
}

// Set SEO metadata for the 404 page
useSeoMeta({
  title: `Page Not Found | ${siteStore.name}`,
  description: 'The page you are looking for could not be found. Please check the URL or navigate back to the homepage.',
  robots: 'noindex, nofollow'
})
</script>